import { useEffect, useMemo, useState } from 'react';

/**
 * Minimal, typed surface over window.Telegram.WebApp.
 *
 * Why not @telegram-apps/sdk-react directly: the SDK is excellent but ships
 * a lot of abstraction for a small surface we actually need (initData,
 * haptics, viewport expand, theme). Keeping this thin hook keeps the bundle
 * small and makes mocking in Playwright trivial.
 */

type HapticStyle = 'light' | 'medium' | 'heavy' | 'rigid' | 'soft';

export type InvoiceStatus = 'paid' | 'cancelled' | 'failed' | 'pending';

type TgWebApp = {
    initData: string;
    initDataUnsafe: {
        user?: TgUser;
        query_id?: string;
        auth_date?: number;
        hash?: string;
        start_param?: string;
    };
    colorScheme: 'light' | 'dark';
    themeParams: Record<string, string>;
    viewportHeight: number;
    viewportStableHeight: number;
    isExpanded: boolean;
    ready(): void;
    expand(): void;
    close(): void;
    onEvent(type: string, cb: () => void): void;
    offEvent(type: string, cb: () => void): void;
    openInvoice?(url: string, callback?: (status: InvoiceStatus) => void): void;
    openLink?(url: string, options?: { try_instant_view?: boolean }): void;
    openTelegramLink?(url: string): void;
    /** Bot API 8.0+: share a message that was pre-saved via
     *  `savePreparedInlineMessage`. Older Telegram clients won't have this
     *  method — callers must fall back to t.me/share/url. */
    shareMessage?(messageId: string, callback?: (sent: boolean) => void): void;
    version?: string;
    HapticFeedback: {
        impactOccurred(style: HapticStyle): void;
        notificationOccurred(type: 'success' | 'warning' | 'error'): void;
        selectionChanged(): void;
    };
    BackButton: { show(): void; hide(): void; onClick(cb: () => void): void };
    MainButton: unknown;
};

export type TgUser = {
    id: number;
    first_name?: string;
    last_name?: string;
    username?: string;
    language_code?: string;
    is_premium?: boolean;
    photo_url?: string;
};

declare global {
    interface Window {
        Telegram?: { WebApp: TgWebApp };
    }
}

export type UseTelegram = {
    webApp: TgWebApp | null;
    user: TgUser | null;
    initData: string;
    startParam: string | null;
    colorScheme: 'light' | 'dark';
    viewportHeight: number;
    haptic: (style?: HapticStyle) => void;
    notify: (type: 'success' | 'warning' | 'error') => void;
    isReady: boolean;
};

export function useTelegram(): UseTelegram {
    const webApp = useMemo(() => window.Telegram?.WebApp ?? null, []);
    const [viewportHeight, setViewportHeight] = useState<number>(
        webApp?.viewportStableHeight ?? window.innerHeight,
    );
    const [isReady, setIsReady] = useState(false);

    useEffect(() => {
        if (!webApp) {
            // Outside Telegram (dev/E2E): mark ready so UI renders.
            setIsReady(true);
            return;
        }
        webApp.ready();
        webApp.expand();
        setIsReady(true);

        const onViewport = () => setViewportHeight(webApp.viewportStableHeight);
        webApp.onEvent('viewportChanged', onViewport);
        return () => webApp.offEvent('viewportChanged', onViewport);
    }, [webApp]);

    // start_param comes from /start <code> or ?startapp=<code> URLs.
    // Falls back to the page's ?ref=<code> query so the dev-mode preview
    // (no Telegram bridge) can still bind referrals.
    const startParam = webApp?.initDataUnsafe.start_param ?? readUrlRef();

    return {
        webApp,
        user: webApp?.initDataUnsafe.user ?? null,
        initData: webApp?.initData ?? '',
        startParam,
        colorScheme: webApp?.colorScheme ?? 'dark',
        viewportHeight,
        haptic: (style = 'medium') => webApp?.HapticFeedback.impactOccurred(style),
        notify: (type) => webApp?.HapticFeedback.notificationOccurred(type),
        isReady,
    };
}

function readUrlRef(): string | null {
    try {
        const qs = new URLSearchParams(window.location.search);
        return qs.get('ref') ?? qs.get('startapp') ?? null;
    } catch {
        return null;
    }
}
